import { type FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { type PersonioConfig } from 'src/modules/Settings/Integrations/pages/components/integrations2.0/Personio/utils';
import { type IntegrationParamOption } from './common';
import { zeusApiV1 } from 'src/store/api';

export type ConnectPersonioArgs = {
  body: { clientId: string; clientSecret: string };
  noAutoLogout: boolean;
  subsidiary?: string;
};

type PersonioCredentialsResponse = {
  message: string;
};

type PersonioParamsResponse = {
  subcompanies: string[];
  departments: string[];
  offices: string[];
  leaveTypes: string[];
  customFields: IntegrationParamOption[];
};

const extendedApi = zeusApiV1.injectEndpoints({
  endpoints: build => ({
    savePersonio: build.mutation<PersonioConfig, Partial<PersonioConfig>>({
      query: args => ({
        url: '/hercules/v1/personio/config',
        method: 'PATCH',
        body: args,
        params: { subsidiary: args.subsidiary }
      }),
      invalidatesTags: ['integration']
    }),
    connectPersonio: build.mutation<PersonioCredentialsResponse, ConnectPersonioArgs>({
      queryFn: async ({ subsidiary, body }, _api, _extraOptions, baseQuery) => {
        try {
          const result = await baseQuery({
            url: '/hercules/v1/personio/credentials',
            method: 'PUT',
            params: { subsidiary },
            body
          });

          if (result.error) {
            return {
              error: {
                ...result.error,
                data: {
                  error: 'integrations.invalid credentials'
                }
              } as unknown as FetchBaseQueryError
            };
          }

          return { data: result.data as PersonioCredentialsResponse };
        } catch (error) {
          console.error('Error fetching integration URL:', error);

          return { error: error as FetchBaseQueryError };
        }
      },
      invalidatesTags: ['integration', 'personio']
    }),
    getPersonioParams: build.query<PersonioParamsResponse, { subsidiary: string }>({
      async queryFn({ subsidiary }, _api, _extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: '/hercules/v1/personio/company-params',
            params: { subsidiary }
          });

          if (result.error) {
            return {
              error: {
                ...result.error,
                data: {
                  error: 'integrations.invalid credentials'
                }
              } as unknown as FetchBaseQueryError
            };
          }

          return { data: result.data as PersonioParamsResponse };
        } catch (error) {
          console.error('Error fetching Personio params:', error);

          return { error: error as FetchBaseQueryError };
        }
      },
      providesTags: ['personio']
    })
  }),
  overrideExisting: false
});

export const { useSavePersonioMutation, useConnectPersonioMutation, useGetPersonioParamsQuery } = extendedApi;
